import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ModalController, NavController, PopoverController} from '@ionic/angular';
import {AccountPaymentFavoriteService} from '@wlp/ui-bs-account-payment-favorite';
import {
  BilletLotService,
  BilletService,
  BilletUpdateAbatementRequestDto,
  BilletUpdateDateDueRequestDto,
  BilletUpdateDiscountRequestDto,
  BilletUpdateInterestRequestDto,
  BilletUpdatePayerRequestDto,
  BilletUpdatePaymentDeadlineRequestDto,
  BilletUpdateProtestRequestDto,
  BilletUpdateRequestNumberRequestDto,
  BilletUpdateTrafficTicketRequestDto,
  BilletUpdateValueRequestDto
} from '@wlp/ui-bs-cashin';
import {UserInfoService} from '@wlp/ui-bs-login';
import {BilletDto, LotWithBilletCompletedDto} from '@wlp/ui-px-cashin';
import {UiBsTranslateService} from '@wlp/ui-bs-translate';
import * as moment from 'moment';
import {LayoutConfigModel} from 'src/app/core/layout/models/layout-config.model';
import {LayoutConfigService} from 'src/app/core/layout/services/layout-config.service';
import {LoadingService} from 'src/app/core/layout/services/loading.service';
import {
  ModalBilletAlertCancelComponent
} from 'src/app/views/themes/sentinel/modals/modal-billet-alert-cancel/modal-billet-alert-cancel';
import {ModalAlertComponent} from 'src/app/views/themes/sentinel/modals/modal-alert/modal-alert.component';
import {ModalTokenComponent} from 'src/app/views/themes/sentinel/modals/modal-token/modal-token.component';
import {tap} from 'rxjs/operators';
import {FormDiscount} from './forms/form-discount/form-discount';
import {FormFees} from './forms/form-fees/form-fees';
import {FormFine} from './forms/form-fine/form-fine';
import {FormDueDate} from './forms/form-due-date/form-due-date';
import {FormNominalValue} from './forms/form-nominal-value/form-nominal-value';
import {FormDeadlinePayment} from './forms/form-deadline-payment/form-deadline-payment';
import {FormDocument} from './forms/form-document/form-document';
import {FormPayer} from './forms/form-payer/form-payer';
import {FormProtest} from './forms/form-protest/form-protest';
import {FormAbatement} from './forms/form-abatement/form-abatement';
import {CpManagerBasePage} from "../../../../cp-manager-base.page";

type EditFieldOptions = 'document' | 'dueDate' | 'nominalValue' | 'discount' | 'fees' | 'fines' | 'paymentDeadline' | 'protest' | 'payer' | 'abatement';
type TypeOrigin = 'list' | 'lot' ; 

@Component({
  selector: 'ui-t-billet-edit',
  templateUrl: './billet-edit.page.html',
  styleUrls: ['./billet-edit.page.scss'],
})
export class CharginBilletEdit  extends CpManagerBasePage  implements OnInit {
  public errorMessages: any;
  private langDefault: string;
  private layoutConfigValue: LayoutConfigModel;

  private readonly OPERATION: string = '1002';
  public toggleSpinnerUpdate: boolean = false;

  @ViewChild(FormFine, {static: false}) public formFine: FormFine;
  @ViewChild(FormFees, {static: false}) public formFees: FormFees;
  @ViewChild(FormDiscount, {static: false}) public formDiscount: FormDiscount;
  @ViewChild(FormDocument, {static: false}) public formDocument: FormDocument;
  @ViewChild(FormDueDate, {static: false}) public formDueDate: FormDueDate;
  @ViewChild(FormDeadlinePayment, {static: false}) public formDeadlinePayment: FormDeadlinePayment;
  @ViewChild(FormNominalValue, {static: false}) public formNominalValue: FormNominalValue;
  @ViewChild(FormPayer, {static: false}) public formPayer: FormPayer;
  @ViewChild(FormProtest, {static: false}) public formProtest: FormProtest;
  @ViewChild(FormAbatement, {static: false}) public formAbatement: FormAbatement;

  public uuidBillet: string;
  public uuidAccountPayment: string;
  public uuidAccountPaymentLotBillet: string;

  public lot: LotWithBilletCompletedDto = null;
  public billet: BilletDto = null;

  public editType: EditFieldOptions = null;
  public typeOrigin: TypeOrigin = null;

  constructor(
    private layoutConfigService: LayoutConfigService,
    private translate: UiBsTranslateService,
    private loadingService: LoadingService,
    public modalController: ModalController,
    public popoverController: PopoverController,
    public router: Router,
    private route: ActivatedRoute,
    private navCtrl: NavController,
    protected userInfoService: UserInfoService,
    public billetService: BilletService,
    public billetLotService: BilletLotService,
    public accountPaymentFavoriteService: AccountPaymentFavoriteService
  ) {
    super(userInfoService);
    moment.locale('pt-br');
  }

  public async ngOnInit() {
    this.setLanguage();
    await this.loadUser();

    this.uuidAccountPayment = this.userProfile.uuidAccount;
    this.uuidBillet = this.route.snapshot.paramMap.get('uuidBillet');

    this.route.queryParams.subscribe(params => {
      this.editType = params['editType'];
      this.typeOrigin = params['typeOrigin'];
    });

    await this.getBillet(this.uuidBillet);
  }

  private setLanguage() {
    this.layoutConfigValue = this.layoutConfigService.getSavedConfig();
    this.langDefault = this.layoutConfigValue.wlTheme.languageDefault;
    this.translate.setDefaultLang(this.langDefault);
  }

  private async getBillet(uuidBillet: string){
    await this.billetService.findByUuidBilletV2(uuidBillet).subscribe( res => {
      this.billet = res;

      if (this.billet.uuidAccountPaymentLotBillet) {
        this.getLot(this.billet.uuidAccountPaymentLotBillet);
        this.uuidAccountPaymentLotBillet = this.billet.uuidAccountPaymentLotBillet;
      }
    });
  }

  private async getLot(uuidLot: string) {
    const lot = await this.billetLotService.getLotWithBilletsByLotUuid(this.uuidAccountPayment, uuidLot).toPromise();
    if (!(lot instanceof Error)) {
      this.lot = lot;
    }
  }

  public async handleClickSaveButton(event?: any) {
    switch (this.editType) {
      case 'dueDate':
        await this.modalConfirmUpdate(() => this.validateToken(this.updateDueDateBillet.bind(this)));
        break;
      case 'nominalValue':
        await this.modalConfirmUpdate(() => this.validateToken(this.updateNominalValueBillet.bind(this)));
        break;
      case 'document':
        await this.modalConfirmUpdate(() => this.validateToken(this.updateRequestNumberBillet.bind(this)));
        break;
      case 'paymentDeadline':
        await this.modalConfirmUpdate(() => this.validateToken(this.updateDeadLineBillet.bind(this)));
        break;
      case 'discount':
        await this.modalConfirmUpdate(() => this.validateToken(this.updateDiscountBillet.bind(this)));
        break;
      case 'fees':
        await this.modalConfirmUpdate(() => this.validateToken(this.updateInterestBillet.bind(this)));
        break;
      case 'fines':
        await this.modalConfirmUpdate(() => this.validateToken(this.updateFinesBillet.bind(this)));
        break;
      case 'payer':
        await this.modalConfirmUpdate(() => this.validateToken(this.updatePayerBillet.bind(this)));
        break;
      case 'protest':
        await this.modalConfirmUpdate(() => this.validateToken(this.updateProtestBillet.bind(this)));
        break;
      case 'abatement':
        await this.modalConfirmUpdate(() => this.validateToken(this.updateAbatementBillet.bind(this)));
        break;
    }
  }

  public async modalConfirmUpdate(onConfirm: VoidFunction) {
    const modalToken = await this.modalController.create({
      component: ModalBilletAlertCancelComponent,
      cssClass: 'modal-billet-alert-cancel',
      componentProps: {
        validationOnly: true,
        codeOperation: this.OPERATION,
        confirmation: true,
        confirm: onConfirm,
        title: 'CASH_OUT.MODAL_EDIT.TITLE',
        description: 'CASH_OUT.MODAL_EDIT.DESCRIPTION',
        alert2: 'CASH_OUT.MODAL_EDIT.ALERT',
        confirmBtn: 'CASH_OUT.MODAL_EDIT.BUTTON1',
        cancelBtn: 'CASH_OUT.MODAL_EDIT.BUTTON2'
      },
      backdropDismiss: false,
    });

    return await modalToken.present();
  }

  public async validateToken(onValidate: (token: string) => void) {
    const modalToken = await this.modalController.create({
      component: ModalTokenComponent,
      cssClass: 'modal-token',
      componentProps: {
        validationOnly: true,
        codeOperation: this.OPERATION,
        confirmation: true,
      },
      backdropDismiss: false,
    });

    modalToken.onDidDismiss().then(data => {
      if (data["data"] && data['role']) {
        onValidate(data.role)
      }
    })
    return await modalToken.present();
  }

  private updateDueDateBillet(codeAuthorization: string) {
    const dueDateFormValue = this.formDueDate.dueDateForm.value.dateDue.trim().split('/').reverse().join('-');
    const body: BilletUpdateDateDueRequestDto = {
      dateDue: dueDateFormValue,
      transactionId: this.billet.transactionId,
      uuidWhitelabel: this.billet.uuidWhiteLabel,
      codeAuthorization: codeAuthorization,
    };

    this.billetLotService.updateDueDate(this.uuidBillet, body).pipe(
      tap((data) => {
        if (data['id'] || data.isExecuted) {
          this.statusNotification('success', 'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE', true);
        } else {
          this.statusNotification('warning-circle', data.message ? this.customMessageReturn(data) :
            'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE_ERROR', true);
        }
        this.loadingService.hide();
      }),
    ).subscribe();
  }

  private updateRequestNumberBillet(codeAuthorization: string) {
    const documentFormValue = this.formDocument.documentForm.value.document;
    const body: BilletUpdateRequestNumberRequestDto = {
      requestNumber: documentFormValue,
      transactionId: this.billet.transactionId,
      uuidWhitelabel: this.billet.uuidWhiteLabel,
      codeAuthorization: codeAuthorization
    };

    this.billetLotService.updateRequestNumber(this.uuidBillet, body).pipe(
      tap((data) => {
        if (data['id'] || data.isExecuted) {
          this.statusNotification('success', 'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE', true);
        } else {
          this.statusNotification('warning-circle', data.message ? this.customMessageReturn(data) :
            'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE_ERROR', true);
        }
        this.loadingService.hide();
      }),
    ).subscribe();
  }

  private updateDeadLineBillet(codeAuthorization) {
    const deadlineFormValue = this.formDeadlinePayment.deadLineControlForm.value.deadLine.trim().split('/').reverse().join('-');
    const body: BilletUpdatePaymentDeadlineRequestDto = {
      deadline: deadlineFormValue,
      transactionId: this.billet.transactionId,
      uuidWhitelabel: this.billet.uuidWhiteLabel,
      codeAuthorization: codeAuthorization,
    };

    this.billetLotService.updateLimitDate(this.uuidBillet, body).pipe(
      tap((data) => {
        if (data['id'] || data.isExecuted) {
          this.statusNotification('success', 'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE', true);
        } else {
          this.statusNotification('warning-circle', data.message ? this.customMessageReturn(data) :
            'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE_ERROR', true);
        }
        this.loadingService.hide();
      }),
    ).subscribe();
  }

  private updateNominalValueBillet(codeAuthorization: string) {
    const nominalValue = this.formNominalValue.nominalValueForm.value.nominalValue;
    this.loadingService.show();

    const body: BilletUpdateValueRequestDto = {
      amount: nominalValue,
      transactionId: this.billet.transactionId,
      uuidWhitelabel: this.billet.uuidWhiteLabel,
      codeAuthorization: codeAuthorization,
    };

    this.billetLotService.updateValue(this.uuidBillet, body).pipe(
      tap((data) => {
        if (data['id'] || data.isExecuted) {
          this.statusNotification('success', 'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE', true);
        } else {
          this.statusNotification('warning-circle', data.message ? this.customMessageReturn(data) :
            'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE_ERROR', true);
        }
        this.loadingService.hide();
      }),
    ).subscribe();
  }

  private updateProtestBillet(codeAuthorization: string) {
    const protestFormValue = this.formProtest.protestForm.value;
    let protestType;
    this.loadingService.show();

    switch(protestFormValue.protestType) {
      case 'EFFECTIVE_PROTEST':
        protestType = 'EFFECTIVE_PROTEST';
        break;

      case 'NOT_PROTEST':
        protestType = 'NOT_PROTEST';
        break;

      case 'CANCEL_PROTEST':
        protestType = 'CANCEL_PROTEST';
        break;
    }

    const body: BilletUpdateProtestRequestDto = {
      type: protestType,
      days: protestFormValue.protestDays,
      transactionId: this.billet.transactionId,
      uuidWhitelabel: this.billet.uuidWhiteLabel,
      codeAuthorization: codeAuthorization,
    };

    this.billetLotService.updateProtest(this.uuidBillet, body).pipe(
      tap((data) => {
        if (data['id'] || data.isExecuted) {
          this.statusNotification('success', 'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE', true);
        } else {
          this.statusNotification('warning-circle', data.message ? this.customMessageReturn(data) :
            'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE_ERROR', true);
        }
        this.loadingService.hide();
      }),
    ).subscribe();
  }

  private updateDiscountBillet(codeAuthorization: string) {
    const discountFormValue = this.formDiscount.discountForm.value;

    discountFormValue.discounts.forEach(discount => {
      const formattedDate = discount.expirationDate.trim().split('/').reverse().join('-');
      discount.expirationDate = formattedDate;
    });

    let discountType: 'NOT_DISCOUNT' | 'FIXED_DISCOUNT' | 'PROPORTIONAL_DISCOUNT' | 'PROPORTIONAL_IN_ADVANCE_DISCOUNT' | 'FIXED_IN_ADVANCE_DISCOUNT';

    if (discountFormValue.discountType === 'NOT_DISCOUNT') {
      discountType = 'NOT_DISCOUNT';
    } else if (discountFormValue.discountType === 'FIXED_DISCOUNT') {
      discountType = 'FIXED_DISCOUNT';
    } else if (discountFormValue.discountType === 'PROPORTIONAL_DISCOUNT') {
      discountType = 'PROPORTIONAL_DISCOUNT';
    }

    const body: BilletUpdateDiscountRequestDto = {
      codeType: discountType,
      discounts: discountFormValue.discounts,
      transactionId: this.billet.transactionId,
      uuidWhitelabel: this.billet.uuidWhiteLabel,
      codeAuthorization: codeAuthorization,
    };

    this.billetLotService.updateDiscount(this.uuidBillet, body).pipe(
      tap((data) => {
        if (data['id'] || data.isExecuted) {
          this.statusNotification('success', 'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE', true);
        } else {
          this.statusNotification('warning-circle', data.message ? this.customMessageReturn(data) :
            'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE_ERROR', true);
        }
        this.loadingService.hide();
      }),
    ).subscribe();
  }

  private updateFinesBillet(codeAuthorization: string) {
    const fines = this.formFine.getFine()

    const body: BilletUpdateTrafficTicketRequestDto = {
      codeType: fines.type,
      amount: fines.value,
      expirationDate: fines.expirationDate as unknown as string,
      transactionId: this.billet.transactionId,
      uuidWhitelabel: this.billet.uuidWhiteLabel,
      codeAuthorization: codeAuthorization,
    };

    this.billetLotService.updateFine(this.uuidBillet, body).pipe(
      tap((data) => {
        if (data['id'] || data.isExecuted) {
          this.statusNotification('success', 'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE', true);
        } else {
          this.statusNotification('warning-circle', data.message ? this.customMessageReturn(data) :
            'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE_ERROR', true);
        }
        this.loadingService.hide();
      }),
    ).subscribe();

  }

  private updatePayerBillet(codeAuthorization: string) {
    const payerFormValues = this.formPayer.payerForm.value;
    this.loadingService.show();

    const body: BilletUpdatePayerRequestDto = {
      namePayer: payerFormValues.name,
      emailPayer: payerFormValues.email,
      documentPayer: payerFormValues.document,
      zipCode: payerFormValues.postalCode,
      street: payerFormValues.address,
      streetNumber: payerFormValues.numberAddress,
      streetComplement: payerFormValues.complementAddress,
      neighborhood: payerFormValues.neighborhood,
      city: payerFormValues.cityAddress,
      state: payerFormValues.uf,
      country: payerFormValues.country,
      transactionId: this.billet.transactionId,
      uuidWhitelabel: this.billet.uuidWhiteLabel,
      codeAuthorization: codeAuthorization,
    };

    this.billetLotService.updatePayer(this.uuidBillet, body).pipe(
      tap((data) => {
        if (data['id'] || data.isExecuted) {
          this.statusNotification('success', 'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE', true);
        } else {
          this.statusNotification('warning-circle', data.message ? this.customMessageReturn(data) :
            'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE_ERROR', true);
        }
        this.loadingService.hide();
      }),
    ).subscribe();
  }

  private updateInterestBillet(codeAuthorization) {
    const fees = this.formFees.getFees()

    const body: BilletUpdateInterestRequestDto = {
      codeType: fees.type,
      amount: fees.value,
      expirationDate: fees.expirationDate.trim().split('/').reverse().join('-') as unknown as string,
      transactionId: this.billet.transactionId,
      uuidWhitelabel: this.billet.uuidWhiteLabel,
      codeAuthorization: codeAuthorization,
    };

    this.billetLotService.updateFees(this.uuidBillet, body).pipe(
      tap((data) => {
        if (data['id'] || data.isExecuted) {
          this.statusNotification('success', 'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE', true);
        } else {
          this.statusNotification('warning-circle', data.message ? this.customMessageReturn(data) :
            'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE_ERROR', true);
        }
        this.loadingService.hide();
      }),
    ).subscribe();
  }

  private updateAbatementBillet(codeAuthorization: string) {
    const billetValueAbatement = this.formAbatement.abatementForm.value.abatement;
    const billetValueAbatementFormatted = parseFloat(billetValueAbatement.replace(',', '.'));
    this.loadingService.show();

    const body: BilletUpdateAbatementRequestDto = {
      billetValueAbatement: billetValueAbatementFormatted,
      transactionId: this.billet.transactionId,
      uuidWhitelabel: this.billet.uuidWhiteLabel,
      codeAuthorization: codeAuthorization,
    };

    this.billetLotService.updateAbatement(this.uuidBillet, body).pipe(
      tap((data) => {
        if (data['id'] || data.isExecuted) {
          this.statusNotification('success', 'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE', true);
        } else {
          this.statusNotification('warning-circle', data.message ? this.customMessageReturn(data) :
            'CASH_OUT.BILLET_EDIT.MODAIS.STATUS_NOTIFICATION_MESSAGE_ERROR', true);
        }
        this.loadingService.hide();
      }),
    ).subscribe();
  }

  private customMessageReturn(data: any){
    let messageReturn = '';
    if (data.message) {
      messageReturn = data.message;
      if (data.responseDetails && data.responseDetails.mensagem ) {
        messageReturn += ' - ' + data.responseDetails.mensagem;

        if (data.responseDetails.campos && data.responseDetails.campos.length > 0) {
          messageReturn += ' - ' + data.responseDetails.campos[0].mensagem;
        }
      }
    }

    return messageReturn;
  }

  private async statusNotification(status: string, message: string, translateFlag: boolean) {
    this.loadingService.hide();
    const translate = translateFlag ? true : false;
    const modalStatus = await this.modalController.create({
      component: ModalAlertComponent,
      cssClass: 'modal-alert-class',
      componentProps: {
        status: status,
        message,
        confirmBtn: 'CASH_OUT.ACCOUNT_CHARGING.CLOSE_BUTTON',
        translate: translate,
        hasActionAfterConfirmation: true,
      },
      backdropDismiss: false,
    });

    modalStatus.onDidDismiss().then(() => {
      this.goBack()
    })

    return await modalStatus.present();
  }

  public goBack(event?: any) {
    if (this.typeOrigin == 'lot') {
      this.navCtrl.navigateRoot(`/app/charging/billet/v2/lot/${this.uuidAccountPayment}/${this.uuidAccountPaymentLotBillet}`);
    } else {
      this.navCtrl.navigateRoot(`/app/charging/billet/v2/list`);
    }
  }
}
